import React, { useRef, useState, forwardRef, useImperativeHandle, ReactNode } from 'react';
import { PhoneWrapper } from '../../components/PhoneWrapper';
import { AppLayout } from '../../components/AppLayout';
import { TopBar } from '../../components/TopBar';
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

type OnboardingProps = {
  children: ReactNode;
};

type OnboardingScreenProps = {
  title?: string;
  subtitle?: string;
  hero: ReactNode;
  children: ReactNode;
};

type OnboardingFooterButtonProps = {
  label: string;
  hidden?: boolean;
  onClick: () => void;
};

const Onboarding = forwardRef<{ nextScreen: () => void; prevScreen: () => void }, OnboardingProps>(({ children }, ref) => {
  const [currentScreen, setCurrentScreen] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const screenCount = React.Children.count(children);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;

    const currentTouch = e.touches[0].clientX;
    const diff = touchStart - currentTouch;

    if (Math.abs(diff) > 50) { // minimum swipe distance
      if (diff > 0 && currentScreen < screenCount - 1) {
        // Swipe left
        setCurrentScreen(prev => prev + 1);
      } else if (diff < 0 && currentScreen > 0) {
        // Swipe right
        setCurrentScreen(prev => prev - 1);
      }
      setTouchStart(null);
    }
  };

  useImperativeHandle(ref, () => ({
    nextScreen: () => setCurrentScreen((prev) => Math.min(prev + 1, screenCount - 1)),
    prevScreen: () => setCurrentScreen((prev) => Math.max(prev - 1, 0)),
  }));

  const screens = React.Children.toArray(children);

  return (
    <div
      style={{ position: 'relative', width: '100%', overflow: 'hidden', height: '100%' }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
    >
      {screens.map((screen, index) => (
        <div
          key={index}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            transition: 'opacity 0.3s ease-in-out',
            opacity: index === currentScreen ? 1 : 0,
            pointerEvents: index === currentScreen ? 'auto' : 'none',
          }}
        >
          {screen}
        </div>
      ))}
      <div style={{
        position: 'absolute',
        bottom: '80px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: '8px',
      }}>
        {screens.map((_, index) => (
          <div
            key={index}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: index === currentScreen ? '#007bff' : '#ccc',
              transition: 'background-color 0.3s',
            }}
          />
        ))}
      </div>
    </div>
  );
});

const Hero = ({ gradients, text }: { gradients: string[]; text: string }) => (
  <div style={{ position: 'relative', width: '100%', paddingTop: '50%', overflow: 'hidden', borderRadius: '10px' }}>
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "#8c8888",
      }}
    />
    {gradients.map((gradient, index) => (
      <div
        key={index}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: gradient,
          opacity: 1,
          mixBlendMode: 'overlay',
        }}
      />
    ))}
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: 'white', textAlign: 'center' }}>
      {text}
    </div>
  </div>
);

const OnboardingScreen = ({ title, subtitle, hero, children }: OnboardingScreenProps) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '20px', boxSizing: 'border-box' }}>
    <div style={{
      backgroundColor: 'white', borderRadius: '15px', padding: '20px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', width: '100%', boxSizing: 'border-box', textAlign: 'center',
      position: "absolute",
      bottom: 0,
      top: 0,
      left: 0,
      right: 0,
    }}>
      {hero}
      {title && <h1 style={{ margin: '10px 0' }}>{title}</h1>}
      {subtitle && <h2 style={{ margin: '10px 0', color: '#555' }}>{subtitle}</h2>}
      {children}
    </div>
  </div>
);

const OnboardingContent = ({ children }: { children: ReactNode }) => (
  <div style={{
    margin: '20px 0',
    overflowY: 'auto',
    maxHeight: 'calc(100% - 200px)',
    width: '100%',
    boxSizing: 'border-box',
    fontSize: '14px',
    textAlign: 'left',
  }}>
    {children}
  </div>
);

const OnboardingFooter = ({ children }: { children: ReactNode }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    position: 'absolute',
    bottom: '20px',
    left: '0',
    padding: '0 20px',
    backgroundColor: 'white',
    boxSizing: 'border-box',
  }}>
    {children}
  </div>
);

const OnboardingFooterButton = ({ label, hidden, onClick }: OnboardingFooterButtonProps) => (
  <button
    onClick={onClick}
    style={{
      padding: '12px',
      width: '48px',
      height: '48px',
      border: 'none',
      backgroundColor: '#007bff',
      color: 'white',
      cursor: 'pointer',
      borderRadius: '50%',
      visibility: hidden ? "hidden" : "visible",
      transition: 'background-color 0.3s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
    onMouseDown={(e) => (e.currentTarget.style.backgroundColor = '#0056b3')}
    onMouseUp={(e) => (e.currentTarget.style.backgroundColor = '#007bff')}
    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#007bff')}
  >
    {label === 'Prev' ? <CaretLeft size={24} /> : <CaretRight size={24} />}
  </button>
);

function LandingPage() {
  return (
    <PhoneWrapper>
      <AppLayout
        top={<TopBar left={<h2>Znovu</h2>} />}
        body={<LandingOnboarding />}
      />
    </PhoneWrapper>
  );
}

const LandingOnboarding = () => {
  const ref = useRef<{ nextScreen: () => void; prevScreen: () => void }>(null);

  return (
    <Onboarding ref={ref}>
      <OnboardingScreen
        hero={<Hero gradients={['linear-gradient(135deg, #ff7e5f, #feb47b)', 'linear-gradient(315deg, #8a2387, #e94057, #f27121)']} text="Welcome" />}
      >
        <OnboardingContent>
          <h3>Welcome to Znovu</h3>
          <p>Your daily habits tracker!</p>
          <p>Our app helps you build and maintain healthy habits by <b>tracking</b> your <b>daily</b> activities and providing a simple, visual way to see <b>your progress.</b></p>
        </OnboardingContent>
        <OnboardingFooter>
          <OnboardingFooterButton label="Prev" hidden={true} onClick={() => ref.current?.prevScreen()} />
          <OnboardingFooterButton label="Next" onClick={() => ref.current?.nextScreen()} />
        </OnboardingFooter>
      </OnboardingScreen>
      <OnboardingScreen
        hero={<Hero gradients={['linear-gradient(135deg, #43e97b, #38f9d7)', 'linear-gradient(315deg, #1e90ff, #ff69b4)']} text="Healthy Habits" />}
      >
        <OnboardingContent>
          <p>Maintaining good habits is essential for mental health and overall well-being. Consistent routines help reduce stress, improve productivity, and enhance your quality of life.</p>
          <ul>
            <li>Books like "Atomic Habits" and "The Talent Code" emphasize the power of small, consistent actions in achieving long-term goals.</li>
          </ul>
        </OnboardingContent>
        <OnboardingFooter>
          <OnboardingFooterButton label="Prev" hidden={true} onClick={() => ref.current?.prevScreen()} />
          <OnboardingFooterButton label="Next" onClick={() => ref.current?.nextScreen()} />
        </OnboardingFooter>
      </OnboardingScreen>
      <OnboardingScreen
        hero={<Hero gradients={['linear-gradient(135deg, #ffa500, #ffff00)', 'linear-gradient(315deg, #ff4500, #ff6347)']} text="How It Works" />}
      >
        <OnboardingContent>
          <p>Each habit has a score, positive or negative. When you complete a habit, toggle it to add its score to your daily total.</p>
          <p>If your total score is positive, the day will be green. If negative, the day will be red.</p>
          <p>This simple visual feedback helps you stay motivated and track your progress over time.</p>
        </OnboardingContent>
        <OnboardingFooter>
          <OnboardingFooterButton label="Prev" hidden={true} onClick={() => ref.current?.prevScreen()} />
          <OnboardingFooterButton label="Next" onClick={() => ref.current?.nextScreen()} />
        </OnboardingFooter>
      </OnboardingScreen>
      <OnboardingScreen
        hero={<Hero gradients={['linear-gradient(135deg, #8a2be2, #4b0082)', 'linear-gradient(315deg, #0000ff, #1e90ff)']} text="Install" />}
      >
        <OnboardingContent>
          <p>Your data is stored locally and never shared. You can export or import your data at any time.</p>
          <p>To install Znovu, tap the "Share" button below, then select "Add to Home Screen".</p>
        </OnboardingContent>
        <OnboardingFooter>
          <div style={{ position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)', fontSize: '24px' }}>
            &#x2193;
          </div>
        </OnboardingFooter>
      </OnboardingScreen>
    </Onboarding>
  );
};

export { Onboarding, OnboardingScreen, OnboardingContent, OnboardingFooter, OnboardingFooterButton, LandingPage };